import {
  closeDetachedWindow,
  openDetachedWindow,
  updateDetachedWindowTitle,
} from "./detachedWindow.js";

const ROUTE_MAP_DETACHED_WINDOW_NAME = "grain-route-map-detached-route-map";

let activeRouteMapPictureInPicture = null;

const DEFAULT_ROUTE_MAP_PIP_WIDTH = 1120;
const DEFAULT_ROUTE_MAP_PIP_HEIGHT = 820;

function prepareRouteMapDetachedDocument(mountNode) {
  const sourceDocument = window.document;
  const targetDocument = mountNode?.ownerDocument;
  if (!targetDocument) {
    return;
  }

  targetDocument.body.className = sourceDocument.body.className || "";
  targetDocument.body.style.margin = "0";
  targetDocument.body.style.minHeight = "100vh";
  targetDocument.body.style.overflow = "hidden";
  targetDocument.body.style.background = "#fff";
  mountNode.style.minHeight = "100vh";
}

export function isRouteMapPictureInPictureSupported() {
  return Boolean(typeof window !== "undefined" && typeof window.open === "function");
}

export function closeRouteMapPictureInPictureWindow() {
  const pipWindow = activeRouteMapPictureInPicture?.pipWindow;
  activeRouteMapPictureInPicture = null;

  if (pipWindow && !pipWindow.closed) {
    closeDetachedWindow(pipWindow);
  }
}

export function isRouteMapPictureInPictureOpen() {
  const pipWindow = activeRouteMapPictureInPicture?.pipWindow;
  return Boolean(pipWindow && !pipWindow.closed && activeRouteMapPictureInPicture?.mountNode);
}

function resolvePictureInPictureFullscreenElement(targetDocument) {
  return targetDocument?.fullscreenElement
    || targetDocument?.webkitFullscreenElement
    || targetDocument?.mozFullScreenElement
    || targetDocument?.msFullscreenElement
    || null;
}

async function requestRouteMapPictureInPictureFullscreen(pictureInPictureWindow) {
  const targetDocument = pictureInPictureWindow?.document || null;
  const targetElement = targetDocument?.documentElement || null;

  if (!targetElement) {
    return false;
  }

  if (typeof targetElement.requestFullscreen === "function") {
    try {
      await targetElement.requestFullscreen({ navigationUI: "hide" });
      return true;
    } catch {
      try {
        await targetElement.requestFullscreen();
        return true;
      } catch {
        return false;
      }
    }
  }

  const prefixedRequestFullscreen = targetElement.webkitRequestFullscreen
    || targetElement.mozRequestFullScreen
    || targetElement.msRequestFullscreen;

  if (typeof prefixedRequestFullscreen !== "function") {
    return false;
  }

  try {
    await prefixedRequestFullscreen.call(targetElement);
    return true;
  } catch {
    return false;
  }
}

async function waitForRouteMapPictureInPictureFocusSettlement(pictureInPictureWindow) {
  const pipWindow = pictureInPictureWindow;
  const targetDocument = pipWindow?.document;
  if (!pipWindow || !targetDocument) {
    return;
  }

  if (!resolvePictureInPictureFullscreenElement(targetDocument)) {
    await new Promise((resolve) => {
      if (typeof pipWindow.requestAnimationFrame === "function") {
        pipWindow.requestAnimationFrame(resolve);
        return;
      }

      pipWindow.setTimeout?.(resolve, 0);
      if (typeof pipWindow.setTimeout !== "function") {
        resolve();
      }
    });
    return;
  }

  await new Promise((resolve) => {
    let settled = false;
    const settle = () => {
      if (settled) {
        return;
      }

      settled = true;
      targetDocument.removeEventListener("fullscreenchange", settle);
      targetDocument.removeEventListener("webkitfullscreenchange", settle);
      if (timeoutId !== null) {
        pipWindow.clearTimeout(timeoutId);
      }
      resolve();
    };

    targetDocument.addEventListener("fullscreenchange", settle);
    targetDocument.addEventListener("webkitfullscreenchange", settle);
    const timeoutId = typeof pipWindow.setTimeout === "function"
      ? pipWindow.setTimeout(settle, 120)
      : null;

    if (typeof pipWindow.requestAnimationFrame === "function") {
      pipWindow.requestAnimationFrame(() => {
        pipWindow.requestAnimationFrame(() => {
          if (!resolvePictureInPictureFullscreenElement(targetDocument)) {
            settle();
          }
        });
      });
    } else if (timeoutId === null) {
      settle();
    }
  });
}

export async function ensureRouteMapPictureInPictureWindowExpanded(
  pictureInPictureWindow = null,
  { forceReapply = false } = {},
) {
  const pipWindow = pictureInPictureWindow || activeRouteMapPictureInPicture?.pipWindow;
  if (!pipWindow || pipWindow.closed) {
    return false;
  }

  if (!forceReapply && resolvePictureInPictureFullscreenElement(pipWindow.document)) {
    return true;
  }

  const screenSize = resolveRouteMapPictureInPictureScreenSize(pipWindow);
  const currentSize = resolveRouteMapPictureInPictureWindowSize(pipWindow);
  const isAlreadyScreenSized = (
    currentSize.width >= screenSize.width - 2
    && currentSize.height >= screenSize.height - 2
  );
  if (!forceReapply && isAlreadyScreenSized) {
    return true;
  }

  const enteredFullscreen = await requestRouteMapPictureInPictureFullscreen(pipWindow);
  if (enteredFullscreen) {
    return true;
  }

  return resizeRouteMapPictureInPictureWindow(
    pipWindow,
    screenSize,
    { useScreenOrigin: true },
  );
}

export async function focusRouteMapPictureInPictureWindow({
  preserveExpanded = false,
  title = null,
} = {}) {
  const pipWindow = activeRouteMapPictureInPicture?.pipWindow;
  if (!pipWindow || pipWindow.closed || !activeRouteMapPictureInPicture?.mountNode) {
    return false;
  }

  if (title) {
    updateDetachedWindowTitle(pipWindow, title);
  }

  try {
    pipWindow.focus?.();
  } catch {
    return false;
  }

  if (preserveExpanded) {
    await waitForRouteMapPictureInPictureFocusSettlement(pipWindow);
    return ensureRouteMapPictureInPictureWindowExpanded(pipWindow, { forceReapply: true });
  }

  return true;
}

export function resolveRouteMapPictureInPictureDefaultSize() {
  return {
    height: DEFAULT_ROUTE_MAP_PIP_HEIGHT,
    width: DEFAULT_ROUTE_MAP_PIP_WIDTH,
  };
}

function resolveHostWindow(candidateWindow = null) {
  if (candidateWindow && typeof candidateWindow === "object") {
    return candidateWindow;
  }

  if (typeof window === "undefined") {
    return null;
  }

  return window;
}

function resolveNumericWindowValue(value, fallback = null) {
  const resolvedValue = Number(value);
  return Number.isFinite(resolvedValue) ? resolvedValue : fallback;
}

function resolveScreenOrigin(hostWindow) {
  const screenRef = hostWindow?.screen || {};
  const left = resolveNumericWindowValue(screenRef.availLeft, null);
  const top = resolveNumericWindowValue(screenRef.availTop, null);

  return {
    left,
    top,
  };
}

function moveRouteMapPictureInPictureWindow(pipWindow, placement = null) {
  if (!pipWindow || typeof pipWindow.moveTo !== "function" || !placement) {
    return false;
  }

  const currentLeft = resolveNumericWindowValue(pipWindow.screenX, null);
  const currentTop = resolveNumericWindowValue(pipWindow.screenY, null);
  const left = resolveNumericWindowValue(placement.left, currentLeft);
  const top = resolveNumericWindowValue(placement.top, currentTop);

  if (!Number.isFinite(left) || !Number.isFinite(top)) {
    return false;
  }

  try {
    pipWindow.moveTo(Math.round(left), Math.round(top));
    return true;
  } catch {
    return false;
  }
}

export function resolveRouteMapPictureInPictureWindowSize(pictureInPictureWindow = null) {
  return {
    height: Math.max(
      1,
      Math.round(Number(pictureInPictureWindow?.outerHeight || pictureInPictureWindow?.innerHeight || 0)),
    ),
    width: Math.max(
      1,
      Math.round(Number(pictureInPictureWindow?.outerWidth || pictureInPictureWindow?.innerWidth || 0)),
    ),
  };
}

export function resolveRouteMapPictureInPictureWindowPlacement(pictureInPictureWindow = null) {
  return {
    ...resolveRouteMapPictureInPictureWindowSize(pictureInPictureWindow),
    left: resolveNumericWindowValue(pictureInPictureWindow?.screenX, null),
    top: resolveNumericWindowValue(pictureInPictureWindow?.screenY, null),
  };
}

export function resolveRouteMapPictureInPictureScreenSize(pictureInPictureWindow = null) {
  const hostWindow = resolveHostWindow(pictureInPictureWindow);
  if (!hostWindow) {
    return resolveRouteMapPictureInPictureDefaultSize();
  }

  const screenRef = hostWindow.screen || {};
  const width = Math.floor(Number(screenRef.availWidth || screenRef.width || DEFAULT_ROUTE_MAP_PIP_WIDTH));
  const height = Math.floor(Number(screenRef.availHeight || screenRef.height || DEFAULT_ROUTE_MAP_PIP_HEIGHT));

  return {
    height: Math.max(DEFAULT_ROUTE_MAP_PIP_HEIGHT, height),
    width: Math.max(DEFAULT_ROUTE_MAP_PIP_WIDTH, width),
  };
}

export function resizeRouteMapPictureInPictureWindow(
  pictureInPictureWindow,
  nextSize,
  {
    placement = null,
    useScreenOrigin = false,
  } = {},
) {
  if (!pictureInPictureWindow || !nextSize) {
    return false;
  }

  const width = Math.max(1, Math.round(Number(nextSize.width || 0)));
  const height = Math.max(1, Math.round(Number(nextSize.height || 0)));
  const currentSize = resolveRouteMapPictureInPictureWindowSize(pictureInPictureWindow);
  let resized = false;

  try {
    if (typeof pictureInPictureWindow.resizeBy === "function") {
      pictureInPictureWindow.resizeBy(
        width - currentSize.width,
        height - currentSize.height,
      );
      resized = true;
    }
  } catch {
    resized = false;
  }

  if (!resized) {
    try {
      if (typeof pictureInPictureWindow.resizeTo === "function") {
        pictureInPictureWindow.resizeTo(width, height);
        resized = true;
      }
    } catch {
      resized = false;
    }
  }

  if (!resized) {
    return false;
  }

  const actualSize = resolveRouteMapPictureInPictureWindowSize(pictureInPictureWindow);
  const requestedDifferentSize = (
    Math.abs(width - currentSize.width) > 1
    || Math.abs(height - currentSize.height) > 1
  );
  const actualSizeChanged = (
    Math.abs(actualSize.width - currentSize.width) > 1
    || Math.abs(actualSize.height - currentSize.height) > 1
  );

  if (requestedDifferentSize && !actualSizeChanged) {
    return false;
  }

  const nextPlacement = useScreenOrigin
    ? resolveScreenOrigin(pictureInPictureWindow)
    : placement;
  moveRouteMapPictureInPictureWindow(pictureInPictureWindow, nextPlacement);
  return true;
}

export async function openRouteMapPictureInPictureWindow({
  height = DEFAULT_ROUTE_MAP_PIP_HEIGHT,
  onClose = null,
  replaceExisting = false,
  title = "Carte routière",
  width = DEFAULT_ROUTE_MAP_PIP_WIDTH,
} = {}) {
  if (!isRouteMapPictureInPictureSupported()) {
    return { ok: false, reason: "detached-window-unsupported" };
  }

  const existingWindow = activeRouteMapPictureInPicture?.pipWindow;
  if (existingWindow && !existingWindow.closed && activeRouteMapPictureInPicture?.mountNode && !replaceExisting) {
    activeRouteMapPictureInPicture.onClose = onClose;
    updateDetachedWindowTitle(existingWindow, title);
    existingWindow.focus?.();
    return {
      ok: true,
      mountNode: activeRouteMapPictureInPicture.mountNode,
      pipWindow: existingWindow,
      reused: true,
    };
  }

  if (existingWindow && !existingWindow.closed && replaceExisting) {
    activeRouteMapPictureInPicture = null;
    closeDetachedWindow(existingWindow);
  }

  try {
    const detachedWindow = openDetachedWindow({
      height,
      name: ROUTE_MAP_DETACHED_WINDOW_NAME,
      title,
      width,
    });

    if (!detachedWindow?.popupWindow || !detachedWindow?.mountNode) {
      return { ok: false, reason: "detached-window-open-failed" };
    }

    const pipWindow = detachedWindow.popupWindow;
    const mountNode = detachedWindow.mountNode;
    prepareRouteMapDetachedDocument(mountNode);

    activeRouteMapPictureInPicture = {
      mountNode,
      onClose,
      pipWindow,
    };

    const handlePictureInPictureClose = () => {
      if (activeRouteMapPictureInPicture?.pipWindow !== pipWindow) {
        return;
      }

      const closeCallback = activeRouteMapPictureInPicture.onClose;
      activeRouteMapPictureInPicture = null;

      if (typeof closeCallback === "function") {
        closeCallback();
      }
    };

    pipWindow.addEventListener?.("pagehide", handlePictureInPictureClose, { once: true });
    pipWindow.addEventListener?.("beforeunload", handlePictureInPictureClose, { once: true });

    return {
      ok: true,
      mountNode,
      pipWindow,
      reused: false,
    };
  } catch (error) {
    return { ok: false, error, reason: "detached-window-open-failed" };
  }
}
